import React from 'react';
import styled, { ThemeProvider, DefaultTheme } from 'styled-components';
import { lightTheme, darkTheme } from './SettingsModal';

// Types
interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  theme: 'light' | 'dark';
  user: {
    firstName: string;
    lastName: string;
    email: string;
    [key: string]: any;
  } | null;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.97);
  color: ${({ theme }) => theme.colors.text};
  border-radius: 2rem;
  width: 98%;
  max-width: 430px;
  min-width: 320px;
  padding: 2.2rem 2.1rem 2rem 2.1rem;
  box-shadow: 0 8px 32px 0 rgba(0,0,0,0.18), 0 2px 4px 0 rgba(0,0,0,0.05);
  border: 1.5px solid rgba(0,0,0,0.10);
  backdrop-filter: blur(10px);
  position: relative;
  transition: box-shadow 0.2s, border 0.2s;
  overflow: visible;
`;

const ModalTitle = styled.h2`
  margin: 0 0 1.2rem 0;
  font-size: 1.08rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: 0.01em;
  background: none;
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.1rem;
  right: 1.4rem;
  background: none;
  border: none;
  font-size: 1.6rem;
  cursor: pointer;
  color: #7b8794;
  width: 2.2rem;
  height: 2.2rem;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s, color 0.18s;
  z-index: 3;

  &:hover, &:focus {
    background: rgba(255,255,255,0.85);
    color: #fff;
    outline: none;
  }

  span, svg {
    display: block;
    font-size: 1.6rem;
    line-height: 1;
    font-weight: 400;
    pointer-events: none;
    transition: color 0.18s;
  }
`;

const InfoGroup = styled.div`
  margin-bottom: 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Label = styled.div`
  font-size: 0.89rem;
  color: ${({ theme }) => theme.colors.secondary || '#7b8794'};
  margin-bottom: 0.18rem;
`;

const Value = styled.div`
  font-size: 1.05rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;


const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose, theme, user }) => {
  if (!isOpen) return null;
  const activeTheme: DefaultTheme = theme === 'dark' ? darkTheme : lightTheme;
  return (
    <ThemeProvider theme={activeTheme}>
      <ModalOverlay>
        <ModalContent onClick={e => e.stopPropagation()}>
          <CloseButton onClick={onClose} aria-label="Close"><span>&times;</span></CloseButton>
          <ModalTitle>Profile</ModalTitle>
          <InfoGroup>
            <Label>Full Name</Label>
            <Value>{user ? `${user.firstName} ${user.lastName}` : '-'}</Value>
          </InfoGroup>
          <InfoGroup>
            <Label>Email Address</Label>
            <Value>{user?.email || '-'}</Value>
          </InfoGroup>
          {/* Add more user info here if available */}
        </ModalContent>
      </ModalOverlay>
    </ThemeProvider>
  );
};

export default ProfileModal;
